import { DataProvider, HttpError } from "react-admin";
import { config } from "../../config";

const AUTH_API_URL = config.authServiceUrl || "https://auth.inite.ai";

export class AuthServiceDataProvider implements DataProvider {
  async getList(resource: string, params: any) {
    const { page, perPage } = params.pagination;
    const token = localStorage.getItem("token");

    if (!token) {
      throw new HttpError("Not authenticated", 401);
    }

    let url = "";
    if (resource === "auth-users") {
      url = `${AUTH_API_URL}/admin/users?page=${page}&limit=${perPage}`;
    } else if (resource === "oauth-clients") {
      url = `${AUTH_API_URL}/admin/oauth-clients`;
    }

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new HttpError("Failed to fetch", response.status);
    }

    const data = await response.json();

    if (resource === "auth-users") {
      return {
        data: data.users,
        total: data.pagination.total,
      };
    } else if (resource === "oauth-clients") {
      return {
        data: (Array.isArray(data) ? data : []).map((record: any) => ({
          ...record,
          id: record.clientId ?? record.id,
          isActive: record.active ?? record.isActive,
        })),
        total: Array.isArray(data) ? data.length : 0,
      };
    } else {
      return {
        data: data,
        total: data.length,
      };
    }
  }

  async getOne(resource: string, params: any) {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new HttpError("Not authenticated", 401);
    }

    let url = "";
    if (resource === "auth-users") {
      url = `${AUTH_API_URL}/admin/users/${params.id}`;
    } else if (resource === "oauth-clients") {
      url = `${AUTH_API_URL}/admin/oauth-clients/${params.id}`;
    }

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new HttpError("Failed to fetch", response.status);
    }

    const data = await response.json();
    if (resource === "oauth-clients" && data) {
      return {
        data: {
          ...data,
          id: data.clientId ?? data.id,
          isActive: data.active ?? data.isActive,
        },
      };
    }
    return { data };
  }

  async getMany(resource: string, params: any) {
    const results = await Promise.all(
      params.ids.map((id: string) => this.getOne(resource, { id })),
    );
    return {
      data: results.map((r) => r.data),
    };
  }

  async getManyReference(resource: string, params: any) {
    return this.getList(resource, params);
  }

  async create(resource: string, params: any) {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new HttpError("Not authenticated", 401);
    }

    let url = "";
    if (resource === "oauth-clients") {
      url = `${AUTH_API_URL}/admin/oauth-clients`;
    }

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(params.data),
    });

    if (!response.ok) {
      throw new HttpError("Failed to create", response.status);
    }

    const data = await response.json();
    return { data };
  }

  async update(resource: string, params: any) {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new HttpError("Not authenticated", 401);
    }

    let url = "";
    let body = params.data;
    if (resource === "auth-users") {
      url = `${AUTH_API_URL}/admin/users/${params.id}/roles`;
    } else if (resource === "oauth-clients") {
      url = `${AUTH_API_URL}/admin/oauth-clients/${params.id}`;
      body = { ...params.data };
      if ("isActive" in body) {
        body.active = body.isActive;
        delete body.isActive;
      }
    }

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new HttpError("Failed to update", response.status);
    }

    const data = await response.json();
    if (resource === "oauth-clients" && data) {
      return { data: { ...data, id: data.clientId ?? data.id } };
    }
    return { data };
  }

  async updateMany(resource: string, params: any) {
    const results = await Promise.all(
      params.ids.map((id: string) =>
        this.update(resource, { id, data: params.data }),
      ),
    );
    return {
      data: results.map((r) => r.data.id),
    };
  }

  async delete(resource: string, params: any) {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new HttpError("Not authenticated", 401);
    }

    const id = params.id;
    if (resource === "oauth-clients" && (id == null || id === "")) {
      throw new HttpError("OAuth client id is required for delete", 400);
    }

    let url = "";
    if (resource === "auth-users") {
      url = `${AUTH_API_URL}/admin/users/${id}`;
    } else if (resource === "oauth-clients") {
      url = `${AUTH_API_URL}/admin/oauth-clients/${id}`;
    }

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const text = await response.text();
      let message = `Failed to delete (${response.status})`;
      try {
        const err = text ? JSON.parse(text) : null;
        if (err?.message) message = err.message;
      } catch {
        if (text) message = text.slice(0, 200);
      }
      throw new HttpError(message, response.status);
    }

    const deletedRecord = params.previousData ?? { id };
    return { data: deletedRecord };
  }

  async deleteMany(resource: string, params: any) {
    const results = await Promise.all(
      params.ids.map((id: string) =>
        this.delete(resource, { id, previousData: { id } }),
      ),
    );
    return {
      data: results.map((r) => r.data.id),
    };
  }
}
